import { SilentRequest } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { DefaultButton, Dialog, DialogFooter, DialogType, MessageBar, MessageBarType, PrimaryButton, Spinner, SpinnerSize } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { panelButtonsStyles } from 'global/Application';
import { appInsights } from 'global/Application/AppInsight';
import { Constants } from 'global/Application/Constants';
import { IPrincipal, useAuthentication } from 'global/Authentication';
import { MsalTokenSource } from 'global/DataSources/MsalTokenSource';
import { ISecurityRole, ISystemUserQuery, ITeamQuery } from 'model/CRM.type';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from "react-router-dom";
import { AppDataSource } from '../../global/DataSources/AppDataSource';
import "./Login.css";


//TODO: move this to useAuthentication context once the other login is clear
const Login = () => {
    const [processing, setProcessing] = useState(false);

    // lets start with silent msal and if we run into any issues, switch to manual.
    const [loginMode, setLoginMode] = useState<"silent" | "manual">("silent");
    const [error, setError] = useState<any>(null);

    const { instance, accounts } = useMsal();
    const { login } = useAuthentication();
    const history = useHistory();
    const location = useSelector(state => state);

    const labelId: string = useId('dialogLabel');
    const subTextId: string = useId('subTextLabel');

    useEffect(() => {
        document.title = "Login";

        if (loginMode === "silent")
            handleLogin();
    }, []);

    const dialogStyles = { main: { maxWidth: 450 } };

    const modalProps = useMemo(
        () => ({
            titleAriaId: labelId,
            subtitleAriaId: subTextId,
            isBlocking: false,
            styles: dialogStyles,
        }),
        [labelId, subTextId],
    );

    const dialogContentProps = {
        type: DialogType.largeHeader,
        title: 'Authentication Required',
        closeButtonAriaLabel: 'Close',
        subText: 'Please authenticate to continue.',
    };


    //TODO: this is really messy routing
    // @ts-ignore
    const from = { ...location.router.location, pathname: location.router.location.pathname.replace(/\/editor\/records\//i, "") } || { pathname: "/" };
    async function logMeIn(accessToken: string) {

        const tokenSource = new MsalTokenSource(instance);
        const dataSource = new AppDataSource(tokenSource);

        const whoamiJson = await dataSource.crm.whoAmI();
        const userJson = await dataSource.crm.query(
            `systemusers?$filter=systemuserid eq ${whoamiJson.UserId}` +
            `&$expand= systemuserroles_association($select=roleid,name),` +
            `teammembership_association($select=teamid,name,businessunitid)`
        ) as ISystemUserQuery[];

        const defaultUser = userJson[0];

        const teams: ITeamQuery[] = await dataSource.crm.query(
            `teams?$select=name&$expand=teamroles_association($select=name)`
        );

        // const teamRoles = defaultUser.teammembership_association.map(t1 => {
        //     return teams.find(t2 => t2.teamid === t1.teamid)!.teamroles_association
        // })

        const teamRoles = defaultUser.teammembership_association.reduce((prev, curr) => {
            const tx = teams.find(t2 => t2.teamid === curr.teamid)!.teamroles_association;
            prev.push(...tx);
            return prev;
        }, [] as ISecurityRole[]);

        const identity: IPrincipal = {
            who: whoamiJson,
            systemUser: defaultUser,
            username: defaultUser.domainname,
            token: accessToken,
            displayName: defaultUser.fullname,
            roles: [...defaultUser.systemuserroles_association, ...teamRoles],
            teams: [...defaultUser.teammembership_association]
        };

        if (identity.systemUser?.systemuserid) {
            appInsights?.setAuthenticatedUserContext(identity.systemUser?.systemuserid, undefined, true);
            appInsights?.trackEvent({
                name: 'Login.identity',
                properties: {
                    systemUser: identity.systemUser,
                    app: Constants.app
                }
            });
        }

        login(identity);
        history.replace(from);
    }

    const handleLogin = () => {

        // Add scopes here for ID token to be used at Microsoft identity platform endpoints.
        const loginRequest: SilentRequest = {
            scopes: ["User.Read"],
            redirectUri: Constants.portal.baseUrl
        };

        const request = {
            ...loginRequest,
            account: accounts[0],
            scopes: Constants.crm.scopes
        };

        // instance.loginRedirect(loginRequest)
        //   .catch((e: any) => {
        //     console.error(e);
        //   });

        const loginPromise = new Promise<string>((resolve, reject) => {
            setProcessing(true);

            if (loginMode === "silent") {

                // Silently acquires an access token which is then attached to a request for Microsoft Graph data
                instance.acquireTokenSilent(request)
                    .then((response: any) => {
                        resolve(response.accessToken)
                    })
                    .catch((e: any) => {
                        reject(e);
                    });

            }

            if (loginMode === "manual") {

                instance.acquireTokenPopup(request)
                    .then((response: any) => {
                        resolve(response.accessToken);
                    })
                    .catch((error: any) => {
                        reject(error);
                    });
            }
        });

        loginPromise.then((accessToken: string) => {
            setProcessing(true);
            return logMeIn(accessToken);

        }).catch((error: any) => {

            if (loginMode === "silent")
                setLoginMode("manual");

            // todo map all errors
            //BrowserAuthError: no_account_error: No account object provided to acquireTokenSilent and no active account has been set. Please call setActiveAccount or provide an account on the request.
            if (error.toString().indexOf('no_account_error:') < 0)
                setError(error.toString());
            else
                setError("Login failed.");

        }).finally(() => {
            setProcessing(false);
        });
    }

    return <Dialog hidden={false} dialogContentProps={dialogContentProps} modalProps={modalProps} minWidth={600}>
        {processing && (
            <div>
                <Spinner label="Processing Login information" labelPosition="right" />
            </div>
        )}
        {!processing && (
            <>
                {error && (
                    <div style={{ margin: '1em' }}>

                        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}                >
                            {error}
                        </MessageBar>
                    </div>
                )}
                <div>
                    Unable to confirm authentication. Click the Authenticate button to authenticate again, or refresh the page.
                </div>
                <DialogFooter>
                    <PrimaryButton text="Authenticate" onClick={() => { handleLogin() }} styles={panelButtonsStyles} />
                    <DefaultButton text="Cancel" disabled styles={panelButtonsStyles} />
                </DialogFooter>
            </>
        )}
    </Dialog>;
}

export default Login